import React, { useCallback, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { Download, Share2 } from 'lucide-react'
import { TableCell, TableRow } from 'components/ui/table'
import { Badge } from 'components/ui/badge'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from 'components/ui/tooltip'
import type {
	LibraryDocumentVersion,
	UserSummary,
} from '../types/documentLibrary.types'
import { downloadAndSave } from '../services/libraryDocumentService'

interface VersionHistoryRowProps {
	documentId: string
	version: LibraryDocumentVersion
	/** Retained for API compatibility; the per-version delete UX moved
	 *  out of this row when the More column was dropped. */
	canDelete?: boolean
	onShare: (versionId: string) => void
	onSoftDelete?: (versionId: string) => void
}

const ownerLabel = (user: LibraryDocumentVersion['uploadedBy']): string => {
	if (!user) return ''
	if (typeof user === 'string') return ''
	const u = user as UserSummary
	const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
	return full || u.email || ''
}

const VersionHistoryRow: React.FC<VersionHistoryRowProps> = ({
	documentId,
	version,
	onShare,
}) => {
	const [downloading, setDownloading] = useState(false)
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)

	const handleDownload = useCallback(async () => {
		if (downloading) return
		setDownloading(true)
		try {
			await downloadAndSave(
				{
					id: documentId,
					versionId: version._id,
					filename: version.originalFilename,
				},
				{
					headers: {
						Authorization: `Bearer ${userInfo?.token || ''}`,
					},
				},
			)
		} finally {
			setDownloading(false)
		}
	}, [documentId, version, downloading, userInfo?.token])

	return (
		<TableRow className="h-12 hover:bg-[#fffbf2]">
			<TableCell className="py-2 text-sm whitespace-nowrap">
				{ownerLabel(version.uploadedBy)}
			</TableCell>
			<TableCell className="py-2 text-sm whitespace-nowrap">
				{version.fileTrueCreatedAt
					? moment(version.fileTrueCreatedAt).format('MM/DD/YY')
					: ''}
			</TableCell>
			<TableCell className="py-2 text-sm text-center">
				v{version.versionNumber}
			</TableCell>
			<TableCell className="py-2 text-sm">
				{version.changeNote || (
					<span className="text-gray-400 italic">—</span>
				)}
			</TableCell>
			<TableCell className="py-2 text-sm text-center">
				{version.isCurrent ? (
					<Badge className="bg-green-100 text-green-700 hover:bg-green-100">
						Yes
					</Badge>
				) : (
					<span className="text-gray-500">No</span>
				)}
			</TableCell>
			<TableCell className="py-2 text-sm text-center">
				<TooltipProvider delayDuration={300}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={() => onShare(version._id)}
								className="text-gray-500 hover:text-[#0080fe] transition-colors"
								aria-label="Share this version"
							>
								<Share2 className="w-4 h-4" />
							</button>
						</TooltipTrigger>
						<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-[11px]">
							Share this version
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</TableCell>
			<TableCell className="py-2 text-sm text-center">
				<TooltipProvider delayDuration={300}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={handleDownload}
								disabled={downloading}
								className="text-gray-500 hover:text-[#0080fe] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
								aria-label="Download this version"
							>
								<Download className="w-4 h-4" />
							</button>
						</TooltipTrigger>
						<TooltipContent className="bg-white text-[#0080fe] shadow-lg text-[11px]">
							{downloading ? 'Downloading…' : 'Download'}
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</TableCell>
		</TableRow>
	)
}

export default VersionHistoryRow
